import axios from "axios";
import { useSyncExternalStore } from "react";

import constants from "../../constants";

/**
 * Novidades do Keepo (/novidades): as mesmas de keepo.bio/novidades, escritas pelo time no Operations. A API busca
 * por nós (contrato: api-keepo-v2's ReleaseController and common\components\Releases) e diz o que é da pessoa: o que
 * ela ainda não viu (a bolinha do menu), o lançamento a anunciar e a reação dela a cada novidade.
 */

export type ReleaseKind = "release" | "improvement" | "fix";

export const REACTIONS = ["sad", "neutral", "happy"] as const;
export type Reaction = (typeof REACTIONS)[number];

export type Release = {
  slug: string;
  kind: ReleaseKind;
  title: string;
  summary?: string | null;
  /** Already cleaned by the API (only text markup). */
  body_html: string;
  /** Only an https address. */
  cta: { label: string; url: string } | null;
  image: { url: string; alt?: string | null; width?: number | null; height?: number | null } | null;
  /** Only with the image, which is its cover. */
  video_url: string | null;
  /** ISO 8601. */
  published_at: string;
  /** The person's own. */
  reaction: Reaction | null;
};

export type ReleasePage = {
  items: Release[];
  meta: { page: number; per_page: number; total: number; last_page: number };
  /** How many of this page came out since the person last opened the list. */
  unseen: number;
  /** The newest release (kind "release") the person wasn't told about yet. */
  announce: Release | null;
  /** The language they were read in (Portuguese while there are none in English): the reactions send it back. */
  lang: string;
};

export const listReleases = async (page: number, lang: string): Promise<ReleasePage> => {
  const response = await axios.get(`${constants.api}/releases`, { params: { page, lang } });
  return response.data.data;
};

export const reactToRelease = async (slug: string, reaction: Reaction, lang: string): Promise<void> => {
  await axios.post(`${constants.api}/releases/${encodeURIComponent(slug)}/reactions`, { reaction, lang });
};

/** What the menu needs everywhere: the dot and the release to announce, from the first page. */
type Status = { unseen: number; announce: Release | null };

const EMPTY: Status = { unseen: 0, announce: null };
let status: Status = EMPTY;
let loading: Promise<void> | undefined;
// goes up each time the list is seen (or the account changes): an answer asked before that is old news
let generation = 0;
const listeners = new Set<() => void>();

const set = (next: Status) => {
  status = next;
  listeners.forEach(listener => listener());
};

export const releasesStatus = {
  get: () => status,

  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  /** Once per login: the editor asks when it opens (a failure only means no dot). */
  load(lang: string): Promise<void> {
    if (!loading) {
      const asked = generation;
      loading = listReleases(1, lang)
        .then(page => asked === generation && set({ unseen: page.unseen, announce: page.announce }))
        .catch(() => undefined)
        .then(() => undefined);
    }
    return loading;
  },

  /** Once the first answer is in (or failed): whoever shows something of their own (the NPS) waits for it. */
  settled(): Promise<void> {
    return loading ?? Promise.resolve();
  },

  /** The list was opened: the dot goes (and the announcement, which the list already shows). */
  seen() {
    generation += 1;
    set(EMPTY);
    return axios.post(`${constants.api}/releases/seen`).catch(() => undefined);
  },

  /** The announcement was closed: it doesn't come back, the dot stays. */
  announced() {
    set({ ...status, announce: null });
    return axios.post(`${constants.api}/releases/announced`).catch(() => undefined);
  },

  /** Logged out: the next account asks again. */
  reset() {
    generation += 1;
    loading = undefined;
    set(EMPTY);
  },
};

export const useReleasesStatus = () => useSyncExternalStore(releasesStatus.subscribe, releasesStatus.get);
